import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService, ApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Loader2, AlertCircle, Save, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Declare global config for IdentityIQ widget
declare global {
  interface Window {
    IDIQ_CREDIT_REPORT_CONFIG?: {
      jwtToken?: string;
      partnerPortal?: boolean;
    };
  }
}

export function CreditReportPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [memberToken, setMemberToken] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const bottomMarkerRef = useRef<HTMLDivElement | null>(null);
  const isProduction = process.env.NODE_ENV === 'production';

  useEffect(() => {
    const fetchMemberToken = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // Get the IdentityIQ JWT token directly from the API
        const response = await apiService.getIdentityIQToken();
        
        if (response.success && response.token) {
          console.log('[IdentityIQ] Token received, membership:', response.membershipNo);
          setMemberToken(response.token);
        } else {
          setError('Failed to get IdentityIQ token');
        }
      } catch (err) {
        console.error('Error fetching IdentityIQ token:', err);
        if (err instanceof ApiError) {
          // Handle specific error cases
          if (err.status === 400) {
            setError('You are not enrolled in IdentityIQ. Please complete enrollment first.');
          } else if (err.status === 404) {
            setError('User not found. Please contact support.');
          } else if (err.status === 401) {
            setError('Authentication failed. Please try logging in again.');
          } else {
            setError(err.message || 'Failed to load credit report');
          }
        } else if (err instanceof TypeError && err.message.includes('CORS')) {
          setError('CORS configuration error. Please contact your system administrator to configure CORS headers on the backend API.');
        } else {
          setError('Failed to load credit report. Please check console for details.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchMemberToken();
  }, [user?.id]);

  useEffect(() => {
    if (!memberToken || !containerRef.current) return;

    // Set up the IdentityIQ widget configuration
    window.IDIQ_CREDIT_REPORT_CONFIG = {
      jwtToken: memberToken,
      partnerPortal: false, // Set to false to enable print and download
    };

    // Load the IdentityIQ script
    const scriptUrl = isProduction
      ? 'https://storage.googleapis.com/idiq-micro-prod/web-api/idiq-credit-report/index.js'
      : 'https://storage.googleapis.com/idiq-micro-stage/web-api/idiq-credit-report/index.js';

    // Check if script is already loaded
    if (!document.querySelector(`script[src="${scriptUrl}"]`)) {
      const script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.onload = () => {
        console.log('[IdentityIQ] Credit report widget loaded successfully');
        // Create the custom element after script loads
        if (containerRef.current && !containerRef.current.querySelector('idiq-credit-report')) {
          const element = document.createElement('idiq-credit-report');
          containerRef.current.appendChild(element);
          
          // Wait 7 seconds for the widget to render, then scroll to bottom to trigger lazy loading
          setTimeout(() => {
            if (bottomMarkerRef.current) {
              console.log('[IdentityIQ] Scrolling to bottom to trigger lazy loading');
              bottomMarkerRef.current.scrollIntoView({ behavior: 'smooth' });
              
              // After 2 seconds, scroll back to top
              setTimeout(() => {
                console.log('[IdentityIQ] Scrolling back to top');
                window.scrollTo({ top: 0, behavior: 'smooth' });
                
                // Enable the Process button after scrolling back up
                setIsDataLoaded(true);
                console.log('[IdentityIQ] Data loaded, Process button enabled');
              }, 2000); // Wait 2 seconds after scroll down, then scroll up
            }
          }, 7000); // Wait 7 seconds for widget initialization
        }
      };
      script.onerror = (e) => {
        console.error('[IdentityIQ] Failed to load credit report widget', e);
        setError('Failed to load credit report widget. Please check console for details.');
      };
      
      document.body.appendChild(script);
      scriptRef.current = script;
    } else {
      // Script already loaded, just create the element
      if (containerRef.current && !containerRef.current.querySelector('idiq-credit-report')) {
        const element = document.createElement('idiq-credit-report');
        containerRef.current.appendChild(element);
        
        // Wait 7 seconds for the widget to render, then scroll to bottom to trigger lazy loading
        setTimeout(() => {
          if (bottomMarkerRef.current) {
            console.log('[IdentityIQ] Scrolling to bottom to trigger lazy loading');
            bottomMarkerRef.current.scrollIntoView({ behavior: 'smooth' });
            
            // After 2 seconds, scroll back to top
            setTimeout(() => {
              console.log('[IdentityIQ] Scrolling back to top');
              window.scrollTo({ top: 0, behavior: 'smooth' });
              
              // Enable the Process button after scrolling back up
              setIsDataLoaded(true);
              console.log('[IdentityIQ] Data loaded, Process button enabled');
            }, 2000); // Wait 2 seconds after scroll down, then scroll up
          }
        }, 7000); // Wait 7 seconds for widget initialization
      }
    }

    // Cleanup function
    return () => {
      // Clean up the custom element
      if (containerRef.current) {
        const element = containerRef.current.querySelector('idiq-credit-report');
        if (element) {
          element.remove();
        }
      }
    };
  }, [memberToken, isProduction]);

  const handleSaveAndProcess = async () => {
    if (!containerRef.current) return;

    try {
      setIsSaving(true);
      setSaveSuccess(false);

      // Get the HTML content from the container
      const htmlContent = containerRef.current.innerHTML;

      if (!htmlContent || htmlContent.trim().length === 0) {
        setError('No report content to save');
        return;
      }

      // Send to backend
      await apiService.saveCreditReportHtml(htmlContent);
      
      setSaveSuccess(true);
      
      // Navigate back to user after successful save
      setTimeout(() => {
        navigate('/');
      }, 1000); // Give user 1 second to see success message
    } catch (err) {
      console.error('Error saving credit report:', err);
      setError(err instanceof ApiError ? err.message : 'Failed to save credit report');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">Loading credit report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center space-y-4 max-w-md">
          <AlertCircle className="h-12 w-12 mx-auto text-destructive" />
          <div>
            <p className="font-semibold text-lg">Failed to Load Credit Report</p>
            <p className="text-sm text-muted-foreground mt-2">{error}</p>
          </div>
          <Button onClick={handleBack}>
            OK
          </Button>
        </div>
      </div>
    );
  }

  if (!memberToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center space-y-4">
          <AlertCircle className="h-12 w-12 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No credit report available</p>
          <Button onClick={handleBack}>
            OK
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Fixed header with actions */}
      <div className="sticky top-0 z-50 bg-background border-b shadow-sm">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button onClick={handleBack} variant="ghost" size="sm" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <h1 className="text-xl font-semibold">3 Bureau Credit Report</h1>
          </div>
          <div className="flex gap-2">
            {saveSuccess && (
              <span className="text-sm text-green-600 flex items-center mr-2">
                ✓ Saved successfully
              </span>
            )}
            <Button
              onClick={handleSaveAndProcess}
              disabled={!isDataLoaded || isSaving}
              className="flex items-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : !isDataLoaded ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  Process
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Scrollable content area */}
      <div className="container mx-auto px-4 py-6">
        {/* Container for the custom element - created dynamically via DOM */}
        {/* Set very large height to allow microfrontend to control its own scrolling */}
        <div ref={containerRef} id="idiq-credit-report-container" style={{ height: '99999px' }}></div>
        
        {/* Bottom marker for lazy loading trigger */}
        <div ref={bottomMarkerRef} id="bottom-marker" style={{ height: '1px', visibility: 'hidden' }}></div>
      </div>
    </div>
  );
}


